import React, { useState, useCallback } from "react";
import * as env from "shared/env";
import { useSelector } from "react-redux";
import { RootState, useAppThunkDispatch } from "redux/store";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import * as Router from "react-router-dom";
import { urls } from "routes/urls";
import { logout } from "redux/slice/authentication";
import { getUserUnsafe } from "redux/selector";
import { adminModeSetView } from "redux/slice/admin-mode";
import { getAdminMode, getViewType } from "redux/selector";
import { styles } from "./style";
import { Avatar } from "component/shared/avatar";
import { useTheme } from "@mui/material/styles";

interface Props extends Router.RouteComponentProps, WithStyles<typeof styles> {
}

const Component: React.FC<Props> = ({ classes = {}, history }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const user = useSelector((state: RootState) => getUserUnsafe(state));
	const adminMode = useSelector((state: RootState) => getAdminMode(state));
	const viewType = useSelector((state: RootState) => getViewType(state));

	// Local state
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	// Callback functions
	const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	}, []);

	const handleMenuClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleLogout = useCallback(() => {
		dispatch(logout());
		setAnchorEl(null);
	}, [dispatch]);

	const handleProfileClick = useCallback(() => {
		history.push(urls.settingsProfile);
		setAnchorEl(null);
	}, [history]);

	const handleAccountClick = useCallback(() => {
		history.push(urls.settingsAccount);
		setAnchorEl(null);
	}, [history]);

	const handlePasswordClick = useCallback(() => {
		history.push(urls.settingsPassword);
		setAnchorEl(null);
	}, [history]);

	const handleSupportClick = useCallback(() => {
		history.push(urls.supportCenter);
		setAnchorEl(null);
	}, [history]);

	const handleViewModeToggle = useCallback(() => {
		dispatch(adminModeSetView(viewType.inverse));
	}, [dispatch, viewType.inverse]);

	if (!user) {
		return null;
	}

	return (
		<>
			<Mui.Button
				className={classes.dropdownButton}
				onClick={handleMenuOpen}
			>
				<Avatar phrase={user.name} src={user.agent?.photoUrl} color={theme.palette.secondary.main}/>
				<Mui.Hidden xsDown>
					<div className={classes.textWrapper}>
						<Mui.Typography noWrap className={classes.line1}>
							{user.name}
						</Mui.Typography>
							<Mui.Typography noWrap className={classes.line2}>
								{user.company}
							</Mui.Typography>
						<Mui.Tooltip title="Copy ID">
							<Mui.Typography
								noWrap
								className={`${classes.line2} ${classes.clientIdText}`}
								component={"span" as any}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									navigator.clipboard.writeText(String(user.clientId));
								}}
							>
								{`Client ID: #${user.clientId}`}
							</Mui.Typography>
						</Mui.Tooltip>
					</div>
					<FeatherIcon fontSize="small">
						<Icons.ChevronDown />
					</FeatherIcon>
				</Mui.Hidden>
			</Mui.Button>
			<Mui.Popper anchorEl={anchorEl} open={!!anchorEl} style={{ zIndex: 1200 }}>
				<Mui.ClickAwayListener onClickAway={handleMenuClose}>
					<Mui.Paper className={classes.paper}>
						<Mui.MenuList>
					<div className={classes.menuTextWrapper}>
						<Mui.Typography noWrap className={classes.line1}>
							{user.name}
						</Mui.Typography>
						<Mui.Typography noWrap className={classes.line2}>
							{user.company}
						</Mui.Typography>
						<div className={classes.clientIdRow}>
							<Mui.Typography noWrap className={classes.line2}>
								{`Client ID: #${user.clientId}`}
							</Mui.Typography>
							<Mui.Tooltip title="Copy">
								<Mui.IconButton
									className={classes.copyButton}
									onClick={() => navigator.clipboard.writeText(String(user.clientId))}
								>
									<Mui.Typography color="primary" style={{marginRight: 5}}>
										Copy
									</Mui.Typography>
									<FeatherIcon fontSize="small" color="primary">
										<Icons.Copy />
									</FeatherIcon>
								</Mui.IconButton>
							</Mui.Tooltip>
						</div>
					</div>
					<Mui.Divider className={classes.divider} />
							{/* MAX-4139 makes this the home for Profile, so Account and Profile have to be
							    able to appear together — today they are mutually exclusive on
							    user.type.agent. Order per the designs: Account, then Profile. */}
							{env.refreshProfile && user.admin && (
								<Mui.MenuItem
									className="ihf-myaccount"
									onClick={handleAccountClick}
								>
											<Mui.Typography className={classes.menuItemText}>
										Account
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							{user.type.agent && (
								<Mui.MenuItem
									className="ihf-profile"
									onClick={handleProfileClick}
								>
											<Mui.Typography className={classes.menuItemText}>
										{env.refreshProfile ? "Profile" : "My Profile"}
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							{!env.refreshProfile && user.admin && !user.type.agent && (
								<Mui.MenuItem
									className="ihf-myaccount"
									onClick={handleAccountClick}
								>
											<Mui.Typography className={classes.menuItemText}>
										Update Account
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							{user.admin && !user.type.agent && (
								<Mui.MenuItem
									className="ihf-changepassword"
									onClick={handlePasswordClick}
								>
											<Mui.Typography className={classes.menuItemText}>
										Change Password
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							{user.reseller.direct && (
								<Mui.MenuItem
									className="ihf-supportcenter"
									onClick={handleSupportClick}
								>
											<Mui.Typography className={classes.menuItemText}>
										Support Center
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							<Mui.Divider className={classes.divider} />
							{adminMode && (
								<Mui.MenuItem onClick={handleViewModeToggle}>
											<Mui.Typography className={classes.menuItemText}>
										View as {viewType.inverse.label}
									</Mui.Typography>
								</Mui.MenuItem>
							)}
							<Mui.MenuItem disableGutters className={classes.signOutMenuItem}>
								<Mui.Button
									variant="contained"
									color="primary"
									className={classes.signOutButton}
									onClick={handleLogout}
									endIcon={<FeatherIcon fontSize="small"><Icons.LogOut /></FeatherIcon>}
								>
									Sign Out
								</Mui.Button>
							</Mui.MenuItem>
						</Mui.MenuList>
					</Mui.Paper>
				</Mui.ClickAwayListener>
			</Mui.Popper>
		</>
	);
};

export const AccountMenu = withStyles(Router.withRouter(Component), styles);
